import React, { useState, useEffect } from 'react';
import "../../../assets/css/patient/VideoCalling.css";

const VideoCalling = () => {
  const [activeTab, setActiveTab]       = useState('patients');
  const [isCallActive, setIsCallActive] = useState(false);
  const [isMuted, setIsMuted]           = useState(false);
  const [isVideoOff, setIsVideoOff]     = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [showNotification, setShowNotification] = useState(false);

  useEffect(() => {
    let interval;
    if (isCallActive) {
      interval = setInterval(() => setCallDuration(prev => prev + 1), 1000);
    } else {
      setCallDuration(0);
    }
    return () => clearInterval(interval);
  }, [isCallActive]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2,'0')}:${secs.toString().padStart(2,'0')}`;
  };

  const toggleCall = () => {
    setIsCallActive(!isCallActive);
    if (!isCallActive) {
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 3000);
    }
  };
  const toggleMute  = () => setIsMuted(!isMuted);
  const toggleVideo = () => setIsVideoOff(!isVideoOff);

  const features = [
    { icon: '🎥', title: 'HD Video Quality',  description: 'Crystal clear video consultations' },
    { icon: '🔒', title: 'Secure & Private',  description: 'End-to-end encrypted calls' },
    { icon: '📱', title: 'Any Device',        description: 'Desktop, tablet, or mobile' },
  ];

  const benefits = {
    patients: [
      { icon: '🏠', title: 'Consult from Home',     text: 'No travel needed - connect with specialists anywhere' },
      { icon: '⏰', title: 'Flexible Scheduling',   text: 'Book appointments that fit your schedule' },
      { icon: '💰', title: 'Cost Effective',        text: 'Save on travel costs and time' },
    ],
    doctors: [
      { icon: '📊', title: 'Grow Your Practice',    text: 'Reach more patients beyond local area' },
      { icon: '🕐', title: 'Manage Your Time',      text: 'Set your own schedule efficiently' },
      { icon: '💻', title: 'Easy Documentation',    text: 'Built-in tools for prescriptions and notes' },
    ],
  };

  return (
    <section className="consultai-vc-wrapper">

      {/* Notification */}
      {showNotification && (
        <div className="consultai-vc-notification">
          <span className="consultai-vc-notif-icon">✅</span>
          <span>Call {isCallActive ? 'started' : 'ended'} successfully!</span>
        </div>
      )}

      {/* ── HEADER ── */}
      <div className="consultai-vc-header-wrapper">
        <div className="consultai-vc-header-content">
          <div className="consultai-vc-header-badge">
            <span className="consultai-vc-header-dot"></span> Virtual Healthcare
          </div>
          <h2 className="consultai-vc-header-title">
            Connect Instantly with <br />
            <span className="consultai-vc-header-highlight">Video Consultations</span>
          </h2>
          <p className="consultai-vc-header-subtitle">
            Secure, seamless, high-quality video calls connecting patients and doctors.
          </p>
        </div>

        <div className="consultai-vc-header-particles">
          {[...Array(8)].map((_, i) => <div key={i} className="consultai-vc-header-particle"></div>)}
        </div>

        <div className="consultai-vc-header-shapes">
          <div className="consultai-vc-header-shape consultai-vc-shape-1"></div>
          <div className="consultai-vc-header-shape consultai-vc-shape-2"></div>
          <div className="consultai-vc-header-shape consultai-vc-shape-3"></div>
        </div>
      </div>

      {/* ── VIDEO DEMO ── */}
      <div className="consultai-vc-demo-container">
        <div className="consultai-vc-demo-card">
          <div className="consultai-vc-main-screen">
            <div className={`consultai-vc-video-placeholder ${isVideoOff ? 'video-off' : ''}`}>
              <div className="consultai-vc-video-icon">{isVideoOff ? '📹' : '👨‍⚕️'}</div>
              {isCallActive && <div className="consultai-vc-call-timer">{formatTime(callDuration)}</div>}
              <div className="consultai-vc-video-status">
                <span className={`consultai-vc-status-dot ${isCallActive ? 'active' : ''}`}></span>
                {isCallActive ? 'Live Consultation' : 'Ready to Connect'}
              </div>
            </div>
            <div className="consultai-vc-controls">
              <button className={`consultai-vc-control-btn consultai-vc-btn-mute ${isMuted ? 'active' : ''}`} onClick={toggleMute}>{isMuted ? '🔇' : '🎤'}</button>
              <button className={`consultai-vc-control-btn consultai-vc-btn-video ${isVideoOff ? 'active' : ''}`} onClick={toggleVideo}>📹</button>
              <button className={`consultai-vc-control-btn consultai-vc-btn-call ${isCallActive ? 'end' : 'start'}`} onClick={toggleCall}>📞</button>
              <button className="consultai-vc-control-btn consultai-vc-btn-more">⚙️</button>
            </div>
          </div>
          <div className="consultai-vc-thumbnail">
            <div className="consultai-vc-thumbnail-video"><div className="consultai-vc-thumbnail-icon">🧑</div></div>
            <div className="consultai-vc-thumbnail-label">You</div>
          </div>
        </div>
      </div>

      {/* ── FEATURES GRID ── */}
      <div className="consultai-vc-features-grid">
        {features.map((f, i) => (
          <div key={i} className="consultai-vc-feature-card">
            <div className="consultai-vc-feature-icon">{f.icon}</div>
            <h3 className="consultai-vc-feature-title">{f.title}</h3>
            <p className="consultai-vc-feature-text">{f.description}</p>
          </div>
        ))}
      </div>

      {/* ── TABS / BENEFITS ── */}
      <div className="consultai-vc-tabs-container">
        <div className="consultai-vc-tabs-header">
          <button
            className={`consultai-vc-tab-btn ${activeTab === 'patients' ? 'consultai-vc-tab-active' : ''}`}
            onClick={() => setActiveTab('patients')}
          >🏥 For Patients</button>
          <button
            className={`consultai-vc-tab-btn ${activeTab === 'doctors' ? 'consultai-vc-tab-active' : ''}`}
            onClick={() => setActiveTab('doctors')}
          >👨‍⚕️ For Doctors</button>
        </div>
        <div className="consultai-vc-tabs-content">
          <div className="consultai-vc-benefits-grid">
            {benefits[activeTab].map((b, i) => (
              <div key={i} className="consultai-vc-benefit-card">
                <div className="consultai-vc-benefit-icon-wrapper">
                  <span className="consultai-vc-benefit-icon">{b.icon}</span>
                </div>
                <div className="consultai-vc-benefit-content">
                  <h4 className="consultai-vc-benefit-title">{b.title}</h4>
                  <p className="consultai-vc-benefit-text">{b.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

    </section>
  );
};

export default VideoCalling;